import { Page, StyleSheet, View } from '@react-pdf/renderer'
import type { Style } from '@react-pdf/types'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps } from 'features/reporting/types'
import React from 'react'

import { PageTracker } from './PageTracker'

const styles = StyleSheet.create({
  page: {
    paddingTop: 16,
    paddingBottom: 40,
    paddingHorizontal: 20,
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
})

const DEFAULT_HEADER_OFFSET = 0
const DEFAULT_FOOTER_PADDING = 60

type BasePageProps = Omit<
  React.ComponentProps<typeof Page>,
  'children' | 'style'
>

type TrackerProps = {
  sectionId: string
  registerOnce?: boolean
  onRender: PdfProps['onRender']
}

export interface ReportPageProps extends BasePageProps {
  children: React.ReactNode
  header?: React.ReactNode
  footer?: React.ReactNode
  pageStyle?: Style | Style[]
  contentStyle?: Style | Style[]
  contentTopOffset?: number
  contentBottomPadding?: number
  tracker?: TrackerProps
}

const toArray = (style?: Style | Style[]): Style[] => {
  if (!style) {
    return []
  }

  return Array.isArray(style) ? style : [style]
}

export const ReportPage: React.FC<ReportPageProps> = ({
  children,
  header,
  footer,
  pageStyle,
  contentStyle,
  contentTopOffset,
  contentBottomPadding,
  tracker,
  size,
  wrap,
  ...restPageProps
}) => {
  const resolvedSize = size ?? 'LETTER'
  const resolvedWrap = wrap ?? true

  const headerOffset = contentTopOffset ?? (header ? DEFAULT_HEADER_OFFSET : 0)
  const footerPadding =
    contentBottomPadding ?? (footer ? DEFAULT_FOOTER_PADDING : 0)

  const pageStyles = [PDFStyles.page, styles.page, ...toArray(pageStyle)]

  const contentStyles = [
    PDFStyles.content,
    styles.content,
    ...toArray(contentStyle),
    {
      paddingTop: headerOffset,
      paddingBottom: footerPadding,
    },
  ]

  return (
    <Page
      {...restPageProps}
      size={resolvedSize}
      wrap={resolvedWrap}
      style={pageStyles}>
      {tracker ? (
        <PageTracker
          sectionId={tracker.sectionId}
          onRender={tracker.onRender}
          registerOnce={tracker.registerOnce}
        />
      ) : null}
      {header}
      <View style={contentStyles}>{children}</View>
      {footer}
    </Page>
  )
}
